import { Command } from 'cmdk';
import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import FlexContainer from 'components/lib/ui/FlexContainer';
import Icon from 'components/lib/ui/Icon';
import Text from 'components/lib/ui/Text';

import { fontSize, fontWeight, radius, spacing, variables } from 'common/lib/theme/dynamic';
import { useCommandRegistry, useGetCommandsByGroup } from 'lib/commands/context';
import type { MonarchCommand, MonarchCommandGroup, MonarchKnownCommand } from 'lib/commands/types';
import { isMonarchAiCommand, isMonarchCommandView } from 'lib/commands/types';
import { formatShortcut, ShortcutType } from 'lib/keyboardShortcuts';

const ICON_SIZE_PX = 16;

type CommandGroupProps = {
  group: MonarchCommandGroup;
  heading?: string;
  rightContent?: React.ReactNode;
  children: React.ReactNode;
};

type CommandPaletteShortcutProps = {
  keys: NonNullable<MonarchCommand['keys']>;
  type: Maybe<ShortcutType>;
};

/**
 * CommandGroup - A compound component pattern for both registering and rendering commands
 *
 * This only renders the commands, it does not register them.
 *
 * Usage:
 * ```tsx
 * <CommandGroup.Root group="navigation" heading="Navigation">
 *   <CommandGroup.Item
 *     id="dashboard"
 *     label="Go to Dashboard"
 *     action={() => navigateTo('/dashboard')}
 *     keys={['g', 'd']}
 *     shortcutType={ShortcutType.SEQUENCE}
 *   />
 * </CommandGroup.Root>
 * ```
 */
export const CommandGroup = {
  /**
   * Root component for a group of commands
   */
  Root: ({
    group,
    heading,
    rightContent,
    children,
  }: React.PropsWithChildren<CommandGroupProps>) => {
    const commands = useGetCommandsByGroup(group);

    // Do not render the group if there are no commands
    if (commands.length === 0) {
      return null;
    }

    // If heading is not passed, render the children without it
    if (!heading) {
      return <>{children}</>;
    }

    return (
      <Group
        heading={
          <FlexContainer alignCenter justifyBetween>
            {heading}
            {rightContent}
          </FlexContainer>
        }
      >
        {children}
      </Group>
    );
  },

  /**
   * Item component for an individual command
   */
  Item: (item: MonarchKnownCommand) => {
    const { id, label, keys, shortcutType, icon, keywords } = item;

    const { toggleCommandPalette, executeCommandAction } = useCommandRegistry();

    const mergedKeywords = useMemo(
      () =>
        // This makes it so the command is searchable by its keys and keywords, if provided
        (keys ?? []).concat(keywords ?? []),
      [keys, keywords],
    );

    const resolvedIcon = useMemo(() => {
      if (isMonarchAiCommand(item)) {
        return <Icon name="stars-filled" />;
      }

      return icon;
    }, [icon, item]);

    const handleSelect = useCallback(() => {
      executeCommandAction(item);

      if (!isMonarchCommandView(item)) {
        toggleCommandPalette();
      }
    }, [item, executeCommandAction, toggleCommandPalette]);

    return (
      <Item
        key={id}
        value={id}
        keywords={mergedKeywords}
        onSelect={handleSelect}
        $isAi={isMonarchAiCommand(item)}
      >
        {resolvedIcon && <IconContainer>{resolvedIcon}</IconContainer>}
        <Content $isAi={isMonarchAiCommand(item)}>
          <Text>{label}</Text>
        </Content>
        {keys && <CommandPaletteShortcut keys={keys} type={shortcutType} />}
      </Item>
    );
  },
};

const CommandPaletteShortcut = ({
  keys,
  type = ShortcutType.COMMAND,
}: CommandPaletteShortcutProps) => {
  // Sequence shortcuts will be a list of strings which will be the sequence (e.g., ['g', 'b']).
  // Commands (the default) will usually be a single string (e.g. ['alt+shift+t', 'alt+x']), so for formatting purposes, we just get the first one.
  const firstShortcut = type === ShortcutType.COMMAND ? keys[0].split('+') : keys;
  const formattedKeys = useMemo(() => formatShortcut(firstShortcut), [firstShortcut]);

  return (
    <ItemShortcut>
      {formattedKeys.map((key, index) => (
        <React.Fragment key={index}>
          {index > 0 && type === ShortcutType.SEQUENCE && (
            <ShortcutSeparator>then</ShortcutSeparator>
          )}
          <KeyDisplay>{key.toUpperCase()}</KeyDisplay>
        </React.Fragment>
      ))}
    </ItemShortcut>
  );
};

const KeyDisplay = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: ${radius.small};
  background: ${variables.color.background.secondary};
  color: ${variables.color.content.secondary};
  padding: 0 ${spacing.xsmall};
  max-height: 24px;
  justify-content: center;
`;

const Group = styled(Command.Group)`
  [cmdk-group-heading] {
    color: ${variables.color.content.secondary};
    font-size: ${fontSize.small};
    font-weight: ${fontWeight.medium};
    padding: ${spacing.xxxsmall} ${spacing.small};
    border-radius: ${radius.small};
    margin-bottom: ${spacing.xxsmall};
    margin-top: ${spacing.xxsmall};
  }
`;

const Item = styled(Command.Item)<{ $isAi: boolean }>`
  cursor: pointer;
  padding: ${spacing.small};
  border-radius: ${radius.small};
  color: ${variables.color.content.primary};
  display: flex;
  align-items: center;
  gap: ${spacing.xsmall};
  position: relative;

  &[data-selected='true'] {
    background: ${variables.color.background.secondary};

    ${KeyDisplay} {
      background: ${variables.color.background.tertiary};
    }
  }

  &:first-child {
    margin-top: ${spacing.xxsmall};
  }

  /* Skip margin when Item is followed by a group heading */
  &:last-child + [cmdk-group] [cmdk-group-heading] {
    margin-top: 0;
  }

  ${({ $isAi }) =>
    $isAi &&
    css`
      font-weight: ${fontWeight.medium};
      color: ${variables.color.content.brand};
    `}
`;

const ItemShortcut = styled.span`
  color: ${variables.color.content.secondary};
  font-size: ${fontSize.xsmall};
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: ${spacing.xxxsmall};
  font-weight: ${fontWeight.medium};
`;

const ShortcutSeparator = styled.span`
  color: ${variables.color.content.secondary};
  margin: 0 ${spacing.xxsmall};
`;

const IconContainer = styled.div`
  width: ${ICON_SIZE_PX}px;
  height: ${ICON_SIZE_PX}px;
  display: flex;
  align-items: center;
  justify-content: center;

  > span {
    width: 100%;
    height: 100%;
  }
`;

const Content = styled.div<{ $isAi: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xxsmall};

  ${({ $isAi }) =>
    $isAi &&
    css`
      font-weight: ${fontWeight.medium};
      color: ${variables.color.content.brand};
    `}
`;
